<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from 'vue'

import './base.css'
import { useFieldContext } from './field-context'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    rows?: number
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    invalid?: boolean
    maxlength?: number
    /** Grow with the content up to `maxRows`, then scroll. */
    autosize?: boolean
    maxRows?: number
    /** Show a remaining-characters counter; requires `maxlength`. */
    showCount?: boolean
  }>(),
  { rows: 3, placeholder: '', maxRows: 12 },
)

const model = defineModel<string>({ default: '' })

const field = useFieldContext()
const fallbackId = useId()
const textarea = ref<HTMLTextAreaElement | null>(null)

const id = computed(() => field?.controlId.value ?? fallbackId)
const isInvalid = computed(() => props.invalid === true || field?.invalid.value === true)
const isRequired = computed(() => field?.required.value === true)
const isDisabled = computed(() => props.disabled === true || field?.disabled.value === true)
const remaining = computed(() =>
  props.maxlength === undefined ? null : props.maxlength - model.value.length,
)

const countId = computed(() => `${id.value}-count`)
const describedBy = computed(() => {
  const ids = field?.describedBy.value?.split(' ') ?? []
  if (props.showCount && props.maxlength !== undefined) ids.push(countId.value)
  return ids.length > 0 ? ids.join(' ') : undefined
})

function resize(): void {
  const element = textarea.value
  if (!element || !props.autosize) return
  element.style.height = 'auto'
  const lineHeight = Number.parseInt(getComputedStyle(element).lineHeight || '20', 10) || 20
  const max = lineHeight * props.maxRows
  element.style.height = `${Math.min(element.scrollHeight, max)}px`
  element.style.overflowY = element.scrollHeight > max ? 'auto' : 'hidden'
}

watch(model, () => void nextTick(resize))

function onInput(event: Event): void {
  model.value = (event.target as HTMLTextAreaElement).value
}

defineExpose({ focus: (options?: FocusOptions) => textarea.value?.focus(options) })
</script>

<template>
  <div class="e-textarea">
    <textarea
      :id="id"
      ref="textarea"
      v-bind="$attrs"
      class="e-control e-textarea__control"
      :rows="rows"
      :value="model"
      :placeholder="placeholder === '' ? undefined : placeholder"
      :disabled="isDisabled"
      :readonly="readonly"
      :maxlength="maxlength"
      :required="isRequired || undefined"
      :aria-invalid="isInvalid ? 'true' : undefined"
      :aria-describedby="describedBy"
      :aria-required="isRequired ? 'true' : undefined"
      @input="onInput"
    />
    <p v-if="showCount && remaining !== null" :id="countId" class="e-textarea__count" aria-live="polite">
      {{ remaining }} characters left
    </p>
  </div>
</template>

<style>
.e-textarea {
  display: flex;
  flex-direction: column;
  gap: var(--erpx-space-1);
  min-width: 0;
}

.e-textarea__control {
  height: auto;
  min-height: var(--erpx-control-h-lg);
  padding-block: var(--erpx-space-2);
  line-height: var(--erpx-leading-normal);
  resize: vertical;
}

.e-textarea__count {
  align-self: flex-end;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
}
</style>
